// Copyright (c) Docugami, Inc. All rights reserved.

import { SelectionModalDescription, SelectionModalDescriptionBold, SelectionModalTitle, SmallButtonLabel } from 'components/common/Fonts';
import { modalSelectionColor, primaryTheme } from 'components/shell/presentation/AppStyles';
import { DgElementButton } from 'components/shell/utils/DgIconButton';
import { useKeyPressHandler } from 'hooks/KeyPressHandler';
import { useModalSelectionActions } from 'hooks/ModalSelection';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import styled from 'styled-components';
import { getFormattedTextComponents } from 'utils/StringUtils';

const ModalRootContainer = styled.div<{modalColor?: string}>`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    background: ${props => props.modalColor ?? modalSelectionColor};
    color: ${primaryTheme.palette.white};
`;

const ActionText = styled(SmallButtonLabel) <{ hidden?: boolean }>`
    display: ${props => props.hidden ? 'none' : 'flex'};
    margin: 10px;
    color: ${primaryTheme.palette.white};
    font-size: 13px;

    :hover {
        text-decoration: underline;
    }
`;

const LightActionText = styled(ActionText)`
    font-size: 11px;

    :not(:hover) {
        color: ${`${primaryTheme.palette.white}b3`};
    }
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    grid-column-start: 1;
    padding-left: 15px;
    justify-content: flex-start;
`;

const DescriptionTextContainer = styled.div`
    grid-column-start: 2;
`;

const ActionTextContainer = styled.div`
    display: flex;
    flex-direction: row;
    grid-column-start: 3;
    padding-right: 10px;
    align-items: center;
    justify-content: flex-end;
`;

interface SelectionModalProps {
    documentId: string;
}

export const SelectionModal = (props: SelectionModalProps): JSX.Element => {
    const isActive = useSelector((state: AppState) => state.documents.modalSelectionContext.isActive);
    const displayContext = useSelector((state: AppState) => state.documents.modalSelectionContext.displayContext);
    const autoConfirm = useSelector((state: AppState) => !!state.documents.modalSelectionContext.autoConfirm);

    const { confirm, cancel } = useModalSelectionActions();

    function getDescriptionText() {
        if (!displayContext) {
            return []
        }

        let targetMessage = displayContext.defaultMessage;
        if (!!displayContext.selectionReady && !!displayContext.selectedMessage) {
            targetMessage = displayContext.selectedMessage;
        }

        if (!!displayContext.formatArgs) {
            return getFormattedTextComponents({
                formatString: targetMessage,
                formatArgs: displayContext.formatArgs,
                createNormalTextCb: createDescriptionText,
                createFormattedTextCb: createBoldDescriptionText
            });
        }
        else {
            return <SelectionModalDescription>{targetMessage}</SelectionModalDescription>
        }
    }

    useKeyPressHandler(isActive ? 'Escape' : undefined, cancel);

    useEffect(() => {
        if (!!autoConfirm && !!displayContext?.selectionReady) {
            confirm();
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [autoConfirm, displayContext])

    return !isActive ? <></> : (
        <ModalRootContainer modalColor={displayContext?.modalColor}>
            <TitleContainer>
                <SelectionModalTitle>{displayContext?.title.toLocaleUpperCase() ?? 'SELECTION MODE'}</SelectionModalTitle>
            </TitleContainer>
            <DescriptionTextContainer>
                {getDescriptionText()}
            </DescriptionTextContainer>
            <ActionTextContainer>
                <DgElementButton onClick={cancel}>
                    <LightActionText>Cancel</LightActionText>
                </DgElementButton>
                <DgElementButton onClick={confirm}>
                    <ActionText hidden={!displayContext?.selectionReady || autoConfirm}>OK</ActionText>
                </DgElementButton>
            </ActionTextContainer>
        </ModalRootContainer>
    );
}

function createDescriptionText(text: string, key: number) {
    return <SelectionModalDescription key={key}>{text}</SelectionModalDescription>;
}

function createBoldDescriptionText(text: string, key: number) {
    return <SelectionModalDescriptionBold key={key}>{text}</SelectionModalDescriptionBold>;
}
